import { useState, useEffect } from 'react';

export interface SMSConfig {
  phoneNumbers: string[];
  enabled: boolean;
}

const DEFAULT_SMS_CONFIG: SMSConfig = {
  phoneNumbers: [],
  enabled: false,
};

export const useSMSConfig = () => {
  const [smsConfig, setSMSConfig] = useState<SMSConfig>(DEFAULT_SMS_CONFIG);

  // Load SMS config from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('continuumlink-sms-config');
      if (stored) {
        const parsed = JSON.parse(stored);
        setSMSConfig({ ...DEFAULT_SMS_CONFIG, ...parsed });
      }
    } catch (error) {
      console.error('Error loading SMS config:', error);
      setSMSConfig(DEFAULT_SMS_CONFIG);
    }
  }, []);

  // Save SMS config to localStorage whenever it changes
  const updateSMSConfig = (newConfig: Partial<SMSConfig>) => {
    const updatedConfig = { ...smsConfig, ...newConfig };
    setSMSConfig(updatedConfig);
    try {
      localStorage.setItem('continuumlink-sms-config', JSON.stringify(updatedConfig));
    } catch (error) {
      console.error('Error saving SMS config:', error);
    }
  };

  const addPhoneNumber = (phoneNumber: string) => {
    // Basic phone number validation and formatting
    const cleaned = phoneNumber.replace(/\D/g, '');
    let formatted = cleaned;
    
    // Add +1 for US numbers if not present
    if (cleaned.length === 10) {
      formatted = `+1${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      formatted = `+${cleaned}`;
    } else if (!cleaned.startsWith('+')) {
      formatted = `+${cleaned}`;
    } else {
      formatted = phoneNumber;
    }

    if (!smsConfig.phoneNumbers.includes(formatted)) {
      updateSMSConfig({
        phoneNumbers: [...smsConfig.phoneNumbers, formatted]
      });
      return true;
    }
    return false;
  };

  const removePhoneNumber = (phoneNumber: string) => {
    updateSMSConfig({
      phoneNumbers: smsConfig.phoneNumbers.filter(num => num !== phoneNumber)
    });
  };

  const toggleEnabled = () => {
    updateSMSConfig({
      enabled: !smsConfig.enabled
    });
  };

  return {
    smsConfig,
    updateSMSConfig,
    addPhoneNumber,
    removePhoneNumber,
    toggleEnabled,
  };
};